import { useCallback, useEffect, useState } from "react";
import {
  connectJira,
  disconnectJira,
  getJiraInstallation,
  refreshJiraProjects,
  JiraApiError,
  type JiraInstallationView,
} from "../lib/api";

/**
 * Jira integration panel — sibling to LinearIntegrationPanel.
 * Auth: Atlassian email + API token + site URL (basic auth).
 *
 * Disconnected: form for { email, apiToken, siteUrl }.
 * Connected: site display, default project, project list, Refresh + Disconnect.
 */
export function JiraIntegrationPanel(): JSX.Element {
  const [installation, setInstallation] = useState<JiraInstallationView | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const inst = await getJiraInstallation();
      setInstallation(inst);
    } catch (err) {
      if (err instanceof JiraApiError && err.status === 401) {
        setInstallation(null);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function handleApiError(err: unknown): void {
    if (err instanceof JiraApiError) {
      if (err.code === "INTEGRATION_NOT_ALLOWED") {
        setNeedsUpgrade(true);
        setError(err.message);
        return;
      }
      setError(err.message);
      return;
    }
    setError(err instanceof Error ? err.message : String(err));
  }

  async function handleConnect(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setNeedsUpgrade(false);
    if (!email.trim() || !apiToken.trim() || !siteUrl.trim()) {
      setError("Email, API token, and site URL are all required.");
      return;
    }
    setBusy(true);
    try {
      const inst = await connectJira({
        email: email.trim(),
        apiToken: apiToken.trim(),
        siteUrl: siteUrl.trim(),
      });
      setInstallation(inst);
      setApiToken(""); // never echo back
      setInfo(`Connected to ${inst.siteName ?? inst.siteUrl}.`);
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(): Promise<void> {
    if (!window.confirm("Disconnect Jira from UAIP? Issue creation will stop."))
      return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await disconnectJira();
      setInstallation(null);
      setInfo("Jira disconnected.");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  async function handleRefresh(): Promise<void> {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const inst = await refreshJiraProjects();
      setInstallation(inst);
      setInfo("Refreshed project list.");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      id="jira"
      className="rounded-xl border border-slate-700 bg-slate-900/60 p-5"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Jira</h2>
          <p className="text-xs text-slate-400">
            File heal &amp; triage proposals as Jira issues. Pro plan and up.
          </p>
        </div>
        {installation ? (
          <span className="rounded-full bg-emerald-900/40 px-3 py-1 text-xs font-medium text-emerald-300">
            Connected
          </span>
        ) : (
          <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-slate-400">
            Not connected
          </span>
        )}
      </header>

      {error && (
        <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
          {error}
          {needsUpgrade && (
            <>
              {" "}
              <a className="underline" href="/account#plan">
                Upgrade to Pro
              </a>
              .
            </>
          )}
        </div>
      )}
      {info && !error && (
        <div className="mb-3 rounded-md border border-emerald-700/60 bg-emerald-900/30 px-3 py-2 text-sm text-emerald-200">
          {info}
        </div>
      )}

      {!loaded ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : installation ? (
        <ConnectedView
          installation={installation}
          onDisconnect={handleDisconnect}
          onRefresh={handleRefresh}
          busy={busy}
        />
      ) : (
        <form onSubmit={handleConnect} className="flex flex-col gap-2">
          <label className="text-xs font-medium text-slate-300">
            Atlassian email
          </label>
          <input
            type="email"
            autoComplete="email"
            spellCheck={false}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
          />
          <label className="mt-1 text-xs font-medium text-slate-300">
            API token
          </label>
          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="ATATT3xFf…"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
          />
          <label className="mt-1 text-xs font-medium text-slate-300">
            Site URL
          </label>
          <input
            type="url"
            spellCheck={false}
            placeholder="https://acme.atlassian.net"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
          />
          <p className="text-xs text-slate-500">
            Create a token at{" "}
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noreferrer noopener"
              className="text-indigo-300 underline"
            >
              id.atlassian.com → API tokens
            </a>
            . Stored encrypted on the server and never echoed back.
          </p>
          <div className="mt-1">
            <button
              type="submit"
              disabled={busy || !email.trim() || !apiToken.trim() || !siteUrl.trim()}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Connecting…" : "Connect Jira"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function ConnectedView(props: {
  installation: JiraInstallationView;
  onDisconnect: () => void;
  onRefresh: () => void;
  busy: boolean;
}): JSX.Element {
  const { installation: inst, onDisconnect, onRefresh, busy } = props;
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-2 gap-3 text-slate-300">
        <Field label="Site" value={inst.siteName ?? inst.siteUrl} />
        <Field label="URL" value={inst.siteUrl} />
        <Field
          label="Connected as"
          value={inst.displayName ?? "(unknown)"}
        />
        <Field
          label="Default project"
          value={
            inst.defaultProjectKey
              ? `${inst.defaultProjectName ?? "?"} (${inst.defaultProjectKey})`
              : "(none — pick one per issue)"
          }
        />
        <Field
          label="Default issue type"
          value={inst.defaultIssueTypeName}
        />
        <Field
          label="Connected"
          value={new Date(inst.createdAt).toLocaleString()}
        />
      </div>

      {inst.projects && inst.projects.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-slate-400">
            Projects visible to this token ({inst.projects.length})
          </p>
          <ul className="flex flex-wrap gap-2">
            {inst.projects.map((p) => (
              <li
                key={p.id}
                className="rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300"
              >
                {p.name} <span className="text-slate-500">({p.key})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-60"
        >
          Refresh projects
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={busy}
          className="rounded-md border border-rose-800 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-900/30 disabled:opacity-60"
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

function Field(props: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {props.label}
      </p>
      <p className="mt-0.5 text-slate-200">{props.value}</p>
    </div>
  );
}
